import { apiDelete, apiGet, apiPost } from "./apiClient";

/** Matches ASP.NET `ProductsController` route: `[Route("api/[controller]")]` → GET/DELETE `/api/Products` */
export const PRODUCTS_API_PATH = "/api/Products";

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.items)) return value.items;
  if (value && Array.isArray(value.data)) return value.data;
  return [];
}

function num(value, fallback = 0) {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Normalizes API row to camelCase ProductDto shape (GET list/detail). */
export function normalizeProduct(row) {
  if (!row || typeof row !== "object") return null;
  const id = row.id ?? row.Id;
  if (id == null) return null;
  return {
    id,
    name: row.name ?? row.Name ?? "",
    slug: row.slug ?? row.Slug ?? "",
    brandId: row.brandId ?? row.BrandId,
    categoryId: row.categoryId ?? row.CategoryId,
    currencyId: row.currencyId ?? row.CurrencyId,
    vendorId: row.vendorId ?? row.VendorId,
    shortDescription: row.shortDescription ?? row.ShortDescription ?? "",
    longDescription: row.longDescription ?? row.LongDescription ?? "",
    basePrice: num(row.basePrice ?? row.BasePrice, 0),
    salePrice: num(row.salePrice ?? row.SalePrice, 0),
    weight: num(row.weight ?? row.Weight, 0),
    length: num(row.length ?? row.Length, 0),
    width: num(row.width ?? row.Width, 0),
    height: num(row.height ?? row.Height, 0),
    minQuantity: num(row.minQuantity ?? row.MinQuantity, 1),
    isActive: Boolean(row.isActive ?? row.IsActive),
    isFeatured: Boolean(row.isFeatured ?? row.IsFeatured),
    isDigital: Boolean(row.isDigital ?? row.IsDigital),
    avgRating: num(row.avgRating ?? row.AvgRating, 0),
    ratingCount: num(row.ratingCount ?? row.RatingCount, 0),
    code: row.code ?? row.Code ?? "",
    barcode: row.barcode ?? row.Barcode ?? "",
    image: row.image ?? row.Image ?? "",
    metaTitile: row.metaTitile ?? row.MetaTitile ?? row.metaTitle ?? "",
    metaDescription: row.metaDescription ?? row.MetaDescription ?? ""
  };
}

/**
 * POST `/api/Products` body — all ProductDto fields from the API except backend defaults
 * on create (IsActive=true, CreatedDate, UpdatedDate).
 */
export function buildProductSavePayload(form, editingId) {
  const payload = {
    id: editingId ?? 0,
    name: form.name,
    slug: form.slug || null,
    brandId: Number(form.brandId),
    categoryId: Number(form.categoryId),
    vendorId: Number(form.vendorId),
    currencyId: Number(form.currencyId),
    shortDescription: form.shortDescription || null,
    longDescription: form.longDescription || null,
    basePrice: Number(form.basePrice || 0),
    salePrice: Number(form.salePrice || 0),
    barcode: form.barcode || null,
    image: form.image || null,
    code: form.code || null,
    minQuantity: Number(form.minQuantity ?? 1),
    weight: Number(form.weight || 0),
    length: Number(form.length || 0),
    width: Number(form.width || 0),
    height: Number(form.height || 0),
    isFeatured: Boolean(form.isFeatured),
    isDigital: Boolean(form.isDigital),
    avgRating: Number(form.avgRating || 0),
    ratingCount: Number(form.ratingCount || 0),
    metaTitile: form.metaTitile || null,
    metaDescription: form.metaDescription || null
  };
  if (editingId) {
    payload.isActive = Boolean(form.isActive);
  }
  return payload;
}

export async function saveProduct(form, editingId) {
  return apiPost(PRODUCTS_API_PATH, buildProductSavePayload(form, editingId));
}

function mapProduct(row) {
  return normalizeProduct(row);
}

function mapBrand(row) {
  if (!row || typeof row !== "object") return null;
  const id = row.id ?? row.Id;
  if (id == null) return null;
  return { id, name: row.name ?? row.Name ?? "" };
}

function mapCategory(row) {
  if (!row || typeof row !== "object") return null;
  const id = row.id ?? row.Id;
  if (id == null) return null;
  return { id, name: row.name ?? row.Name ?? "" };
}

/**
 * Loads products plus brand/category names for the list grid (GET `/api/Products`, `/api/Brands`, `/api/Categories`).
 */
export async function fetchProductsListView() {
  const [pRes, bRes, cRes] = await Promise.all([
    apiGet(PRODUCTS_API_PATH),
    apiGet("/api/Brands"),
    apiGet("/api/Categories")
  ]);

  const products = asArray(pRes.ok ? pRes.data : null).map(mapProduct).filter(Boolean);
  const brands = asArray(bRes.ok ? bRes.data : null).map(mapBrand).filter(Boolean);
  const categories = asArray(cRes.ok ? cRes.data : null).map(mapCategory).filter(Boolean);

  const ok = Boolean(pRes.ok);
  const error = !pRes.ok ? (pRes.error ?? "Failed to load products") : null;

  return { ok, products, brands, categories, error };
}

/** Soft-deactivate product (API sets IsActive = false). */
export async function deleteProductById(id) {
  return apiDelete(`${PRODUCTS_API_PATH}/${id}`);
}
